import { useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../store';
import { 
  clearPendingCallLogUpdate,
} from '../../lib/redux/sheets/slice';
import { useSpreadsheetApiCall } from './useSpreadsheetApiCall';
import { useEnsureTrellusColumns } from './useEnsureTrellusColumns';

// Dispositions that indicate no human was reached
const NO_HUMAN_DISPOSITIONS = [
  'NO_ANSWER',
  'LEFT_VOICEMAIL', 
  'WRONG_NUMBER'
];

export function useCallLogHandler() {
  const dispatch = useDispatch();
  const { makeApiCall } = useSpreadsheetApiCall();
  const { ensureTrellusColumns } = useEnsureTrellusColumns();
  const auth = useSelector((state: RootState) => state.googleSheets.auth);
  const windowMessages = useSelector((state: RootState) => state.googleSheets.windowMessages);
  const spreadsheetData = useSelector((state: RootState) => state.googleSheets.spreadsheetData);
  
  const pendingCallLogUpdate = windowMessages.pendingCallLogUpdate;
  const tokenConfig = auth.tokenConfig;
  const spreadsheetAccess = spreadsheetData.spreadsheetAccess;

  // Helper function to find the correct sheet by gid - fall back to first sheet if only one exists
  const findSheetByGid = useCallback((sheets: any[], gid?: string) => {
    
    if (!sheets || sheets.length === 0) return null;
    
    // If only one sheet exists, use it regardless of gid
    if (sheets.length === 1) {
      return sheets[0];
    }
    
    if (gid) {
      const targetSheet = sheets.find(sheet => {
        const sheetId = sheet.sheetId || sheet.properties?.sheetId;
        const sheetIdStr = sheetId?.toString();
        const match = sheetIdStr === gid;
        return match;
      });
      return targetSheet || null;
    }
    
    // If no gid provided, return null to force explicit sheet selection
    return null;
  }, []);

  // Helper function to apply call log data to the spreadsheet
  const applyCallLogUpdate = useCallback(async (spreadsheetId: string, callLogData: any, sheetGid?: string): Promise<{ success: boolean; error?: string }> => {
    try {
      // First, ensure Trellus columns are added to this sheet before applying the log
      console.log(`🔍 [CallLogHandler] Ensuring TRELLUS columns exist before applying call log`, { spreadsheetId, sheetGid });
      
      const columnsEnsured = await ensureTrellusColumns(spreadsheetId, sheetGid);
      if (!columnsEnsured) {
        console.error(`❌ [CallLogHandler] Failed to ensure TRELLUS columns exist - CANNOT PROCEED WITH CALL LOGGING`, { 
          spreadsheetId, 
          sheetGid,
          reason: 'ensureTrellusColumns returned false'
        });
        return { success: false, error: 'Failed to ensure Trellus columns exist' };
      }
      
      console.log(`✅ [CallLogHandler] TRELLUS columns confirmed to exist, proceeding with call log update`);

      // Get cached spreadsheet data from Redux (Note: this might be stale after adding columns)
      const cachedSpreadsheet = spreadsheetData.allSpreadsheetData.find(sheet => sheet.id === spreadsheetId);
      
      let workingSpreadsheet: any;
      
      if (!cachedSpreadsheet || !cachedSpreadsheet.data) {
        // Fetch basic spreadsheet metadata if not cached
        const metadataUrl = `https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}?fields=spreadsheetId,properties.title,sheets(properties/title,properties/sheetId)`;
        const metadataResult = await makeApiCall({ url: metadataUrl });
        
        if (!metadataResult.success || !metadataResult.data) {
          return { success: false, error: 'Failed to fetch spreadsheet metadata' };
        }
        
        // Use the fetched metadata to build a minimal spreadsheet object
        workingSpreadsheet = {
          id: metadataResult.data.spreadsheetId,
          name: metadataResult.data.properties.title,
          sheets: metadataResult.data.sheets.map((sheet: any) => ({
            title: sheet.properties.title,
            sheetId: sheet.properties.sheetId,
          })),
          data: [] // We'll get headers separately
        };
      } else {
        workingSpreadsheet = cachedSpreadsheet;
      }

      // Get fresh header data to ensure we have the latest columns
      
      const targetSheet = findSheetByGid(workingSpreadsheet.sheets || [], sheetGid);
      if (!targetSheet) {
        throw new Error(`No target sheet found for gid: ${sheetGid}. Available sheets: ${workingSpreadsheet.sheets?.map((s: any) => `${s.title} (${s.sheetId})`).join(', ')}`);
      }

      const sheetName = targetSheet.title;
      if (!sheetName) {
        throw new Error('No sheet name found in target sheet');
      }

      // Fetch current headers to ensure we have the latest column structure
      const headerUrl = `https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}/values/${encodeURIComponent(sheetName)}!1:1`;
      const headerResult = await makeApiCall({ url: headerUrl });
      
      const headers = headerResult.success && headerResult.data?.values?.[0] 
        ? headerResult.data.values[0] 
        : workingSpreadsheet.data[0] || [];

      // CONTACT_ID is the row index (0-based, excluding header row)
      const contactId = callLogData.CONTACT_INFO?.CONTACT_ID;
      
      if (contactId === undefined || contactId === null) {
        throw new Error('No CONTACT_ID found in call log data');
      }
      

      // CONTACT_ID is 0-based row index (excluding header)
      // For Google Sheets API, we need 1-based indexing and account for header row
      const rowNumber = contactId + 2; // +1 for header row, +1 for 1-based indexing
      
      // Only validate against cached data if we have it
      if (workingSpreadsheet.data && workingSpreadsheet.data.length > 0) {
        const maxValidContactId = workingSpreadsheet.data.length - 2; // -1 for header, -1 for 0-based
        
        if (contactId >= workingSpreadsheet.data.length - 1 || contactId < 0) {
          throw new Error(`Invalid CONTACT_ID ${contactId}. Cached data has ${workingSpreadsheet.data.length - 1} data rows.`);
        }
        
      } else {
      }

      // Helper function to find column index and create cell update
      const getColumnIndex = (columnName: string): number => {
        const index = headers.findIndex((header: any) => header && header.trim() === columnName);
        if (index === -1) {
          console.warn(`⚠️ [CallLogHandler] Column '${columnName}' not found in headers`, { 
            columnName, 
            availableHeaders: headers,
            spreadsheetId,
            sheetGid 
          });
        }
        return index;
      };

      // Helper function to convert column index to letter (A, B, C, AA, AB, etc.)
      const columnIndexToLetter = (index: number): string => {
        let result = '';
        let columnIndex = index;
        
        while (columnIndex >= 0) {
          result = String.fromCharCode(65 + (columnIndex % 26)) + result;
          columnIndex = Math.floor(columnIndex / 26) - 1;
        }
        return result;
      };

      // Helper function to update a single cell
      const updateCell = async (columnName: string, value: string): Promise<boolean> => {
        const startTime = Date.now();
        
        const colIndex = getColumnIndex(columnName);
        if (colIndex === -1) {
          console.error(`❌ [CallLogHandler] Cannot update cell - column '${columnName}' not found`, { 
            columnName, 
            value, 
            spreadsheetId, 
            sheetGid 
          });
          return false;
        }

        const columnLetter = columnIndexToLetter(colIndex);
        const cellRange = `${sheetName}!${columnLetter}${rowNumber}`;
        
        const updateUrl = `https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}/values/${encodeURIComponent(cellRange)}?valueInputOption=RAW`;
        const updateResult = await makeApiCall({
          url: updateUrl,
          method: 'PUT',
          body: {
            values: [[value]]
          }
        });

        const duration = Date.now() - startTime;
        if (!updateResult.success) {
          console.error(`❌ [CallLogHandler] Failed to update cell`, { 
            columnName, 
            value, 
            cellRange, 
            spreadsheetId, 
            sheetGid 
          });
          return false;
        }

        return true;
      };

      // Helper function to get current cell value and append to it
      const appendToCell = async (columnName: string, newValue: string, separator: string = '\n'): Promise<boolean> => {
        const startTime = Date.now();
        
        const colIndex = getColumnIndex(columnName);
        if (colIndex === -1) {
          console.error(`❌ [CallLogHandler] Cannot append to cell - column '${columnName}' not found`, { 
            columnName, 
            newValue, 
            separator, 
            spreadsheetId, 
            sheetGid 
          });
          return false;
        }

        const columnLetter = columnIndexToLetter(colIndex);
        const cellRange = `${sheetName}!${columnLetter}${rowNumber}`;
        
        // Get current value
        const getCurrentUrl = `https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}/values/${encodeURIComponent(cellRange)}`;
        
        const currentResult = await makeApiCall({ url: getCurrentUrl });
        
        const currentValue = currentResult.data?.values?.[0]?.[0] || '';
        
        const finalValue = currentValue ? `${currentValue}${separator}${newValue}` : newValue;
        
        return await updateCell(columnName, finalValue);
      };

      // Helper function to increment a numeric cell value
      const incrementCell = async (columnName: string): Promise<boolean> => {
        const startTime = Date.now();
        
        const colIndex = getColumnIndex(columnName);
        if (colIndex === -1) {
          console.error(`❌ [CallLogHandler] Cannot increment cell - column '${columnName}' not found`, { 
            columnName, 
            spreadsheetId, 
            sheetGid 
          });
          return false;
        }

        const columnLetter = columnIndexToLetter(colIndex);
        const cellRange = `${sheetName}!${columnLetter}${rowNumber}`;
        
        // Get current value
        const getCurrentUrl = `https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}/values/${encodeURIComponent(cellRange)}`;
        
        const currentResult = await makeApiCall({ url: getCurrentUrl });
        
        const currentValue = currentResult.data?.values?.[0]?.[0] || '0';
        const currentNumber = parseInt(currentValue) || 0;
        const newNumber = currentNumber + 1;
        
        
        return await updateCell(columnName, newNumber.toString());
      };
      
      // 1. Set last call attempt to current time
      const currentTime = new Date().toISOString();
      await updateCell('TRELLUS_Last_Call_Attempt', currentTime);

      // 2. Check if human was connected and update last time human if so
      const outcome = callLogData.CALL_INFO?.OUTCOME;
      const humanConnected = outcome && !NO_HUMAN_DISPOSITIONS.includes(outcome);
      
      
      if (humanConnected) {
        await updateCell('TRELLUS_Last_Time_Human', currentTime);
      } else {
      }

      // 3. Update call attempts (increment by 1)
      await incrementCell('TRELLUS_Total_Call_Attempts');

      // 4. Add call notes if they exist
      const callSummary = callLogData.CALL_INFO?.OUTCOMES_PLATFORM_SPECIFIC?.SUMMARY;
      
      if (callSummary && callSummary.trim()) {
        await appendToCell('TRELLUS_Call_Notes', callSummary, '\n');
      } else {
      }

      // 5. Set last call outcome
      const disposition = callLogData.CALL_INFO?.OUTCOMES_PLATFORM_SPECIFIC?.DISPOSITION;
      
      if (disposition) {
        await updateCell('TRELLUS_Last_Call_Outcome', disposition);
      } else {
      }

      // 6. Add transcript URL to previous calls
      const sessionId = callLogData.CALL_INFO?.SESSION?.session_id;
      
      if (sessionId) {
        const transcriptUrl = `https://app.trellus.ai/transcripts?id=${sessionId}`;
        await appendToCell('TRELLUS_All_Previous_Calls', transcriptUrl, ', ');
      } else {
      }

      const totalDuration = Date.now() - (Date.now() - 1000); // Approximate total time
      
      return { success: true };

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      return { success: false, error: errorMessage };
    }
  }, [makeApiCall, spreadsheetData.allSpreadsheetData, ensureTrellusColumns, findSheetByGid]);

  // Handle call log update requests
  const handleCallLogUpdateRequest = useCallback(async (spreadsheetId: string, updateData: any, sheetGid?: string) => {
    
    try {
      if (!tokenConfig) {
        console.error(`❌ [GoogleSheets] No token config available for call log update`);
        return;
      }

      // Check if we have access to this spreadsheet first
      const hasAccess = spreadsheetAccess.includes(spreadsheetId);
      
      if (!hasAccess) {
        console.error(`❌ [GoogleSheets] No access to spreadsheet ${spreadsheetId}`);
        return;
      }

      // Apply the call log update
      const result = await applyCallLogUpdate(spreadsheetId, updateData, sheetGid);
      
      if (!result.success) {
        console.error(`❌ [GoogleSheets] Call log update failed in handleCallLogUpdateRequest`, { 
          spreadsheetId, 
          sheetGid, 
          error: result.error 
        });
      } else {
      }
      
    } catch (error) {
      console.error(`❌ [GoogleSheets] Exception in handleCallLogUpdateRequest`, error);
      console.error(`❌ [GoogleSheets] Exception details`, { 
        spreadsheetId, 
        sheetGid, 
        error: error instanceof Error ? error.message : 'Unknown error',
        errorStack: error instanceof Error ? error.stack : undefined
      });
    } finally {
      dispatch(clearPendingCallLogUpdate());
    }
  }, [tokenConfig, spreadsheetAccess, dispatch, applyCallLogUpdate]);

  // Handle pending call log update requests
  useEffect(() => {
    if (!pendingCallLogUpdate || !tokenConfig) {
      if (!pendingCallLogUpdate) {
      }
      if (!tokenConfig) {
      }
      return;
    }
    
    
    handleCallLogUpdateRequest(pendingCallLogUpdate.spreadsheetId, pendingCallLogUpdate.data, pendingCallLogUpdate.sheetGid);
  }, [pendingCallLogUpdate, tokenConfig, handleCallLogUpdateRequest]);
} 